import { faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import { useApi } from '@hooks/useApi';
import { EmptyState } from '@ui';
import Loader from 'components/Loader';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useConfirmedCallback } from '~/hooks/useConfirmedCallback';
import FileUploadZone from './FileUploadZone';
import MemberFileRow from './MemberFileRow';
import { MemberFile } from './types';
import { useFileUploads } from './useFileUploads';

// Mirrors StoreMembershipFileRequest: members may only hand in PDFs and pictures.
const ACCEPT = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/heic', '.heic'];

/** The member's own view: what the club shared plus their own uploads. */
export const MembershipFiles = ({ clubMemberId }: { clubMemberId: number }) => {
    const { t } = useTranslation('member-management');
    const api = useApi();
    const key = `membership/${clubMemberId}/files`;
    const { data, isLoading, mutate } = useSWR<{ data: MemberFile[]; meta: { can_upload: boolean } }>(key);
    const files = data?.data ?? [];
    const canUpload = !!data?.meta?.can_upload;

    const refresh = useCallback(() => void mutate(), [mutate]);
    const { pending, upload, dismiss } = useFileUploads(key, refresh);

    const openFile = (file: MemberFile) =>
        api({
            url: `${key}/${file.id}/url`,
            onSuccess: ({ url }: { url: string }) => window.open(url, '_blank', 'noopener')
        });

    const deleteFile = useConfirmedCallback(
        (file: MemberFile) => api({ url: `${key}/${file.id}`, method: 'DELETE', onSuccess: refresh }),
        {
            destructive: true,
            message: (file: MemberFile) => t('memberships.files.deleteConfirm', { title: file.display_title })
        }
    );

    return (
        <div className="tw-flex tw-flex-col tw-gap-3">
            {canUpload && (
                <FileUploadZone
                    name={`membership_files_${clubMemberId}`}
                    accept={ACCEPT}
                    pending={pending}
                    onFiles={upload}
                    onDismiss={dismiss}
                />
            )}
            <Loader isLoading={isLoading}>
                {files.length === 0 ? (
                    <EmptyState size="sm" icon={faFolderOpen} title={t('memberships.files.empty')} />
                ) : (
                    <div>
                        {files.map((file) => (
                            <MemberFileRow
                                key={file.id}
                                file={file}
                                onOpen={openFile}
                                onDelete={file.can_delete ? deleteFile : undefined}
                            />
                        ))}
                    </div>
                )}
            </Loader>
        </div>
    );
};

export default MembershipFiles;
